import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import theme from "../../theme";
import { StyledLink } from "../Breadcrumbs";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { useMonitoringList } from "../../data/useMonitoringList";
import { Monitoring } from "../../types/Monitoring";
import { PagedResponse } from "../../types/Common";
import { Image } from "../Image";
import success from "../../assets/card-success-icon.svg";
import inReview from "../../assets/card-in-review-icon.svg";
import pending from "../../assets/pending-icon.svg";
import { Loader } from "../Loader";
import { Paragraph } from "../Paragraph";
import {
  useServerTable,
  TablePagination,
  SortIndicator,
  pageCountFor,
} from "../ServerTable";
import { SubjectNameCell } from "../SubjectNameCell";
import { useFillViewportHeight } from "../../hooks/useFillViewportHeight";
// import { NoScopeSelectedNotice } from "../NoScopeSelectedNotice";

export const MonitoringTable = (props) => {
  const {
    searchTerm,
    filterStatusIds,
    filters,
    hasFilterChips,
    type,
    initialSorting,
    initialPageSize,
    onSortChange,
    onPageSizeChange,
  } = props;

  // Fill the space left below the page chrome, reserving room for the
  // pagination row that renders after the table.
  const { ref: tableRef, height: tableHeight } = useFillViewportHeight({
    bottomGutter: 80,
    remeasureKey: hasFilterChips,
  });

  const [data, setData] = useState<Partial<PagedResponse<Monitoring[]>>>({});
  const [filtersUpdate, setFiltersUpdate] = useState({ ...filters });
  const [searchTermUpdate, setSearchTermUpdate] = useState(props.searchTerm);
  const [filterStatusIdsUpdate, setFilterStatusIdsUpdate] = useState(
    props.filterStatusIds
  );
  const [reference] = useState(props.reference);

  const [monitoringType] = useState(type);

  const monitoringIcons = {
    active: success,
    "requires review": inReview,
    pending: pending,
  };

  const cols: ColumnDef<Monitoring>[] = [
    {
      header: () => "Name",
      accessorKey:
        props.type === "entity"
          ? "company_name"
          : props.type === "individual"
          ? "first_name"
          : "first_name, company_name",
      cell: (info) => {
        const { company_name, first_name, last_name } = info.row.original;
        return (
          <SubjectNameCell
            type={props.type}
            companyName={company_name}
            firstName={first_name}
            lastName={last_name}
          />
        );
      },
    },
    {
      header: () => "Status",
      accessorKey: "status",
      cell: (info) => {
        const { status } = info.row.original;
        // the status is nullable in the backend, and a record without one
        // must not take the whole list down
        if (!status) return <Flex color={theme.colors.gray[40]}>-</Flex>;
        return (
          <Flex justifyContent="start" alignItems="center" style={{ whiteSpace: "nowrap" }}>
            <span key={status.id} style={{ margin: "0 5px" }}>
              <Image
                src={monitoringIcons[status.status]}
                alt="checks"
                height="auto"
                width="22px"
                mr={1}
              />

              {status.label}
            </span>
          </Flex>
        );
      },
    },
    {
      header: () => "Division",
      accessorKey: "primary_division",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_division?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Team",
      accessorKey: "primary_team",
      cell: (info) => {
        return (
          <Flex color={theme.colors.gray[40]}>
            {info.row.original.primary_team?.display_name ?? "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "Reference",
      accessorKey: "reference",
      cell: (info) => {
        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
          >
            {info.row.original.reference}
          </Flex>
        );
      },
    },
    {
      header: () => "External Reference",
      accessorKey: "external_reference",
      size: 180,
      minSize: 180,
      maxSize: 180,
      enableSorting: false,
      cell: (info) => {
        const value = info.row.original.external_reference || "-";

        return (
          <Flex
            justifyContent="start"
            alignItems="center"
            position="relative"
            color={theme.colors.gray[40]}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={String(value)}
          >
            {String(value)}
          </Flex>
        );
      },
    },
    {
      header: () => "Date Started",
      accessorKey: "created_on",
      cell: (info) => {
        const { created_on } = info.row.original;
        return (
          <Flex color={theme.colors.gray[40]}>
            {created_on ? DateTime.fromISO(created_on).toFormat("dd/MM/yyyy") : "_"}
          </Flex>
        );
      },
    },
    {
      header: () => "Last Activity",
      accessorKey: "last_activity",
      cell: (info) => {
        const { last_activity } = info.row.original;
        return (
          <Flex color={theme.colors.gray[40]}>
            {last_activity
              ? DateTime.fromISO(last_activity).toFormat("dd/MM/yyyy")
              : "-"}
          </Flex>
        );
      },
    },
    {
      header: () => "",
      id: "view_id",
      accessorKey: "id",
      cell: (info) => {
        const { id } = info.row.original;
        let path = `${id}`;
        if (info.row.original.status?.status !== "pending") {
          return (
            <StyledLink to={path}>
              <span
                style={{
                  color: theme.colors.blue.primary,
                }}
              >
                View
              </span>
            </StyledLink>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const [columns] = useState([...cols]);
  const {
    pageIndex,
    pageSize,
    pagination,
    setPagination,
    sorting,
    setSorting,
    sortOrder,
    sortBy,
    resetPagination,
  } = useServerTable(initialSorting, initialPageSize, onSortChange, onPageSizeChange);

  const getMonitoringData = useMonitoringList(
    reference,
    filterStatusIdsUpdate,
    searchTermUpdate,
    filtersUpdate,
    pageIndex + 1,
    pageSize,
    sortOrder,
    sortBy,
    monitoringType
  );

  useEffect(() => {
    resetPagination();
    setFiltersUpdate(filters);
    setSearchTermUpdate(searchTerm);
    setFilterStatusIdsUpdate(filterStatusIds);
  }, [searchTerm, searchTermUpdate, filterStatusIds, filters, resetPagination]);

  useEffect(() => {
    setData(() => getMonitoringData?.data?.data);
  }, [getMonitoringData]);

  const table = useReactTable({
    data: data?.results?.filter((row) => !row.deleted) ?? [],
    columns,
    state: {
      pagination,
      sorting,
    },
    pageCount: pageCountFor(data?.count, pageSize),
    debugTable: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {/* Hidden for now — see NoScopeSelectedNotice. */}
      {/* <NoScopeSelectedNotice /> */}
      {/* One fixed-height area for loading, empty and loaded states, so the
          pagination below stays in the footer whichever one is showing. It
          always renders, so it (not the table) carries the measuring ref. */}
      <Box ref={tableRef} height={tableHeight}>
        {getMonitoringData.isFetching && (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Loader />
          </Flex>
        )}

        {!getMonitoringData.isFetching && data?.results?.length === 0 && (
          <Paragraph m={3} color="gray.40" textAlign="center">
            No Monitoring Data
          </Paragraph>
        )}

        {!getMonitoringData.isFetching && data?.results?.length > 0 && (
          <div className="tableContainer" style={{ height: tableHeight }}>
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup?.id}>
                    {headerGroup?.headers.map((header) => (
                      <th key={header?.id} colSpan={header?.colSpan}>
                        {header?.isPlaceholder ? null : (
                          <>
                            <div
                              {...{
                                className: header?.column.getCanSort()
                                  ? "select-none cursor-pointer flex items-center gap-1"
                                  : "",
                                onClick: header?.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <SortIndicator column={header.column} />
                            </div>
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table?.getRowModel().rows.map((row, index) => (
                  <tr key={row?.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell?.id}>
                        {flexRender(
                          cell?.column.columnDef.cell,
                          cell?.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Box>
      <TablePagination table={table} />
    </div>
  );
};
